// Academic Level Types and Utilities

export type EducationLevel = 'secondary' | 'undergraduate' | 'masters' | 'phd';
export type LearningStyle = 'visual' | 'auditory' | 'kinesthetic' | 'reading-writing';

export interface AcademicProfile {
  educationLevel: EducationLevel;
  fieldOfStudy: string;
  learningStyle?: LearningStyle;
  academicGoals: string[];
  currentGPA?: number;
  graduationYear?: number;
  institution?: string;
}

export interface UIComplexity {
  vocabLevel: 'simple' | 'intermediate' | 'advanced' | 'expert';
  explanationDepth: 'basic' | 'detailed' | 'comprehensive' | 'exhaustive';
  visualAids: 'frequent' | 'moderate' | 'occasional' | 'data-driven';
  gamification: 'high' | 'moderate' | 'low' | 'minimal';
  citationRequired: boolean;
  researchTools?: boolean;
  publicationSupport?: boolean;
}

/**
 * Get UI complexity settings based on education level
 */
export function getUIComplexity(level: EducationLevel): UIComplexity {
  switch(level) {
    case 'secondary':
      return {
        vocabLevel: 'simple',
        explanationDepth: 'basic',
        visualAids: 'frequent',
        gamification: 'high',
        citationRequired: false
      };
    case 'undergraduate':
      return {
        vocabLevel: 'intermediate',
        explanationDepth: 'detailed',
        visualAids: 'moderate',
        gamification: 'moderate',
        citationRequired: true
      };
    case 'masters':
      return {
        vocabLevel: 'advanced',
        explanationDepth: 'comprehensive',
        visualAids: 'occasional',
        gamification: 'low',
        citationRequired: true,
        researchTools: true
      };
    case 'phd':
      return {
        vocabLevel: 'expert',
        explanationDepth: 'exhaustive',
        visualAids: 'data-driven',
        gamification: 'minimal',
        citationRequired: true,
        researchTools: true,
        publicationSupport: true
      };
  }
}

/**
 * Get AI system prompt based on education level
 */
export function getSystemPromptByLevel(userLevel: EducationLevel, subject?: string): string {
  const subjectText = subject ? ` specializing in ${subject}` : '';
  const basePrompt = `You are an AI tutor${subjectText}.`;
  
  switch(userLevel) {
    case 'secondary':
      return `${basePrompt}
        
TEACHING STYLE:
- Use simple, everyday language (avoid jargon)
- Include visual analogies (compare to sports, video games, daily life)
- Break complex topics into 3-5 bite-sized chunks
- Use emojis and encouraging language
- Provide step-by-step examples
- Check understanding frequently ("Does that make sense?")
- Celebrate small wins

EXAMPLE FORMAT:
"Think of [concept] like [relatable analogy]. For example, [simple example]."`;
      
    case 'undergraduate':
      return `${basePrompt}
        
TEACHING STYLE:
- Use academic vocabulary with brief definitions
- Provide both theoretical foundations and practical applications
- Include real-world case studies
- Encourage critical thinking with thought-provoking questions
- Reference textbooks and credible sources
- Assign practice problems with varying difficulty

EXAMPLE FORMAT:
"[Concept definition]. This is important because [real-world application]. 
Let's explore this through [case study]..."`;
      
    case 'masters':
      return `${basePrompt}
        
TEACHING STYLE:
- Use advanced terminology and academic discourse
- Compare multiple theoretical frameworks
- Discuss current research and debates in the field
- Analyze methodologies and their limitations
- Provide extensive citations (APA, MLA, Chicago style)
- Guide literature review and research design
- Challenge assumptions and encourage original thought

EXAMPLE FORMAT:
"According to [Author, Year], [theory]. However, [Counter-argument from Author2, Year].
Current research suggests [synthesize multiple sources]..."`;
      
    case 'phd':
      return `${basePrompt}
        
TEACHING STYLE:
- Engage in scholarly discourse at expert level
- Discuss cutting-edge research and unpublished findings
- Analyze research gaps and propose novel investigations
- Critique methodologies rigorously
- Provide comprehensive citation networks
- Guide research design, data analysis, and publication strategy
- Facilitate hypothesis development and testing
- Support grant proposal writing

EXAMPLE FORMAT:
"The current literature (cf. [Author1, Year]; [Author2, Year]) converges on [finding], 
yet methodological concerns persist regarding [issue]. Your research could address this gap by..."`;
  }
}

/**
 * Get education level display information
 */
export function getEducationLevelInfo(level: EducationLevel) {
  const info = {
    secondary: {
      label: 'Secondary School',
      icon: '🎒',
      ageRange: '12-18',
      description: 'High school students building foundational knowledge',
      color: 'from-blue-500 to-cyan-500'
    },
    undergraduate: {
      label: 'Undergraduate',
      icon: '🎓',
      ageRange: '18-22',
      description: 'College/university students pursuing bachelor\'s degree',
      color: 'from-purple-500 to-pink-500'
    },
    masters: {
      label: 'Master\'s',
      icon: '📚',
      ageRange: '22-30',
      description: 'Graduate students pursuing advanced degrees',
      color: 'from-orange-500 to-red-500'
    },
    phd: {
      label: 'PhD/Doctoral',
      icon: '🔬',
      ageRange: '25-35+',
      description: 'Doctoral candidates conducting original research',
      color: 'from-indigo-500 to-purple-600'
    }
  };

  return info[level];
}

// Cache for academic profile to provide sync access
let profileCache: AcademicProfile | null = null;
let profileCachePromise: Promise<AcademicProfile | null> | null = null;

/**
 * Store academic profile in Supabase database
 */
export async function saveAcademicProfile(profile: AcademicProfile): Promise<void> {
  const { supabase } = await import('@/integrations/supabase/client');
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .from('academic_profiles')
    .upsert({
      id: user.id,
      education_level: profile.educationLevel,
      field_of_study: profile.fieldOfStudy,
      learning_style: profile.learningStyle,
      academic_goals: profile.academicGoals,
      current_gpa: profile.currentGPA,
      graduation_year: profile.graduationYear,
      institution: profile.institution,
    });

  if (error) throw error;
  
  // Update cache
  profileCache = profile;
}

/**
 * Load academic profile from Supabase database (async)
 */
export async function loadAcademicProfileAsync(): Promise<AcademicProfile | null> {
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('academic_profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error || !data) return null;

    const profile: AcademicProfile = {
      educationLevel: data.education_level as EducationLevel,
      fieldOfStudy: data.field_of_study || '',
      learningStyle: data.learning_style as LearningStyle | undefined,
      academicGoals: data.academic_goals || [],
      currentGPA: data.current_gpa,
      graduationYear: data.graduation_year,
      institution: data.institution,
    };
    
    // Update cache
    profileCache = profile;
    return profile;
  } catch (error) {
    console.error('Error loading academic profile:', error);
    return null;
  }
}

/**
 * Load academic profile synchronously from cache (use for immediate access)
 * Initialize cache first by calling loadAcademicProfileAsync() or calling initializeAcademicProfile()
 */
export function loadAcademicProfile(): AcademicProfile | null {
  return profileCache;
}

/**
 * Initialize academic profile cache on app startup
 */
export async function initializeAcademicProfile(): Promise<void> {
  if (!profileCachePromise) {
    profileCachePromise = loadAcademicProfileAsync();
  }
  await profileCachePromise;
}

/**
 * Get feature availability based on education level
 */
export function getFeatureAccess(level: EducationLevel) {
  return {
    citationManager: level === 'undergraduate' || level === 'masters' || level === 'phd',
    researchHub: level === 'masters' || level === 'phd',
    publicationTools: level === 'phd',
    advancedAnalytics: level === 'masters' || level === 'phd',
    collaborationNetworks: level === 'phd',
    gpaCalculator: level === 'undergraduate' || level === 'masters',
    gamificationFeatures: level === 'secondary' || level === 'undergraduate'
  };
}
